import type { SponsoredDisplayContextualTarget } from "@adpilot/shared";
import { IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import type { SponsoredDisplayContextualRecommendations } from "@/lib/campaign-creation-api";
import {
  CandidateRow,
  CandidateWorkspace,
  EmptyCandidates,
  LoadingRows,
  TruncatedNotice,
} from "./CandidateWorkspace";

const ASIN_PATTERN = /^[A-Z0-9]{10}$/;

type AddContextualTarget = (label: string, target: SponsoredDisplayContextualTarget) => void;

export function ContextualCategoryCandidates({
  data,
  loading,
  onAdd,
}: {
  data?: SponsoredDisplayContextualRecommendations;
  loading: boolean;
  onAdd: AddContextualTarget;
}) {
  const { t } = useTranslation("campaignCreation");
  const [tab, setTab] = useState("suggestions");
  const [search, setSearch] = useState("");

  function addCategory(
    category: SponsoredDisplayContextualRecommendations["categories"]["items"][number],
  ) {
    onAdd(category.name, {
      type: "category",
      categoryId: category.categoryId,
      refinements: {
        brandId: null,
        price: null,
        reviewRating: null,
        primeShippingOnly: false,
      },
      bid: null,
    });
  }

  return (
    <CandidateWorkspace>
      <Tabs value={tab} onValueChange={setTab} className="p-4">
        <TabsList>
          <TabsTrigger value="suggestions">{t("sd.contextual.suggestions")}</TabsTrigger>
          <TabsTrigger value="search">{t("sd.contextual.search")}</TabsTrigger>
        </TabsList>
        <TabsContent value="suggestions" className="flex flex-col gap-3 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("sd.audience.dynamicSegments")}
          </p>
          <CandidateRow
            label={t("sd.contextual.similar")}
            onAdd={() => onAdd(t("sd.contextual.similar"), { type: "similar_product", bid: null })}
          />
          <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("sd.contextual.productCategories")}
          </p>
          {loading ? (
            <LoadingRows />
          ) : (data?.categories.items.length ?? 0) === 0 ? (
            <EmptyCandidates />
          ) : (
            data?.categories.items.map((category) => (
              <CandidateRow
                key={category.categoryId}
                label={category.name}
                description={category.path.join(" › ")}
                onAdd={() => addCategory(category)}
              />
            ))
          )}
          {data?.categories.truncated ? <TruncatedNotice /> : null}
        </TabsContent>
        <TabsContent value="search" className="flex flex-col gap-3 pt-3">
          <InputGroup>
            <InputGroupAddon>
              <IconSearch />
            </InputGroupAddon>
            <InputGroupInput
              aria-label={t("sd.audience.search")}
              placeholder={t("sd.audience.search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </InputGroup>
          {data?.categories.items
            .filter((category) =>
              category.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
            )
            .map((category) => (
              <CandidateRow
                key={category.categoryId}
                label={category.name}
                description={category.path.join(" › ")}
                onAdd={() => addCategory(category)}
              />
            ))}
          {(data?.categories.items.length ?? 0) === 0 ? <EmptyCandidates /> : null}
        </TabsContent>
      </Tabs>
    </CandidateWorkspace>
  );
}

export function ContextualProductCandidates({
  data,
  input,
  loading,
  onInputChange,
  onAdd,
}: {
  data?: SponsoredDisplayContextualRecommendations;
  input: string;
  loading: boolean;
  onInputChange: (value: string) => void;
  onAdd: AddContextualTarget;
}) {
  const { t } = useTranslation("campaignCreation");
  const [tab, setTab] = useState("suggestions");
  const customAsins = [
    ...new Set(
      input
        .toUpperCase()
        .split(/[\s,]+/)
        .filter(Boolean),
    ),
  ];
  const invalidAsins = customAsins.filter((asin) => !ASIN_PATTERN.test(asin));
  return (
    <CandidateWorkspace>
      <Tabs value={tab} onValueChange={setTab} className="p-4">
        <TabsList>
          <TabsTrigger value="suggestions">{t("sd.contextual.suggestions")}</TabsTrigger>
          <TabsTrigger value="search">{t("sd.contextual.search")}</TabsTrigger>
          <TabsTrigger value="enter">{t("sd.contextual.enter")}</TabsTrigger>
        </TabsList>
        <TabsContent value="suggestions" className="flex flex-col gap-2 pt-3">
          {loading ? (
            <LoadingRows />
          ) : data?.products.items.length ? (
            data.products.items.map((item) => (
              <CandidateRow
                key={item.asin}
                label={item.asin}
                onAdd={() => onAdd(item.asin, { type: "product", asin: item.asin, bid: null })}
              />
            ))
          ) : (
            <EmptyCandidates />
          )}
          {data?.products.truncated ? <TruncatedNotice /> : null}
        </TabsContent>
        <TabsContent value="search" className="flex flex-col gap-3 pt-3">
          <Input
            aria-label={t("sd.contextual.searchByAsin")}
            placeholder={t("sd.contextual.searchByAsin")}
            value={input}
            onChange={(event) => onInputChange(event.target.value)}
          />
          {customAsins
            .filter((asin) => ASIN_PATTERN.test(asin))
            .map((asin) => (
              <CandidateRow
                key={asin}
                label={asin}
                onAdd={() => onAdd(asin, { type: "product", asin, bid: null })}
              />
            ))}
        </TabsContent>
        <TabsContent value="enter" className="flex flex-col gap-3 pt-3">
          <Field data-invalid={invalidAsins.length > 0}>
            <FieldLabel htmlFor="sd-contextual-asins">{t("sd.contextual.enter")}</FieldLabel>
            <Textarea
              id="sd-contextual-asins"
              placeholder={t("sd.contextual.enterPlaceholder")}
              value={input}
              onChange={(event) => onInputChange(event.target.value)}
            />
            {invalidAsins.length > 0 ? (
              <FieldError match={true}>
                {t("sd.contextual.invalidAsin", { values: invalidAsins.join(", ") })}
              </FieldError>
            ) : null}
          </Field>
          <div className="flex justify-end">
            <Button
              type="button"
              disabled={customAsins.length === 0 || invalidAsins.length > 0}
              onClick={() => {
                for (const asin of customAsins) {
                  onAdd(asin, { type: "product", asin, bid: null });
                }
              }}
            >
              {t("sd.actions.add")}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </CandidateWorkspace>
  );
}
